import { type Objectish } from "immer";
import { v4 as uuidV4 } from "uuid";
import {
	type ActorLogic,
	type ActorSystem,
	type PromiseActorLogic,
	assign,
	type createActor,
	setup,
	waitFor,
} from "xstate";

import { type StateTransaction } from "../api-canvas-metadata/StateTransaction";
import { type CanvasSpecifier } from "../api-types";
import { type LoggerInterface } from "../logger-types";
import { type StateSyncServerContext } from "./StateSyncServerContext";
import { type StateSyncServerEvent } from "./StateSyncServerEvent";
import { makeProcessTransactionActor } from "./makeProcessTransactionActor";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyActorSystem = ActorSystem<any>;

export interface InitializeMachineEvent {
	type: string;
	extra: {
		authorizationHeader?: string | null;
		canvasSpecifier: CanvasSpecifier;
	};
}

export interface ResolveTransactionOutput<TData extends Objectish> {
	stateTransaction: StateTransaction;
	resolvedData: TData;
}

export interface StateSyncServerMachineArgs<TData extends Objectish> {
	fetchState: PromiseActorLogic<{ version: number; data: TData }, InitializeMachineEvent>;
	resolveTransaction: (
		transaction: StateTransaction,
		input: TData,
		version: number
	) => Promise<ResolveTransactionOutput<TData>>;
	validateTransaction?: (transaction: StateTransaction, input: TData, result: unknown) => boolean;
	persist: PromiseActorLogic<void, { context: StateSyncServerContext<TData>; data: TData }>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	publish: ActorLogic<any, any, unknown, AnyActorSystem>;
	logger: LoggerInterface;
}

export type StateSyncServerMachine<TData extends Objectish> = ReturnType<
	typeof makeStateSyncServerMachine<TData>
>;

export function makeStateSyncServerMachine<TData extends Objectish>({
	fetchState,
	resolveTransaction,
	validateTransaction,
	persist,
	publish,
	logger,
}: StateSyncServerMachineArgs<TData>) {
	return setup({
		actions: {},
		actors: {
			fetchState,
			processTransaction: makeProcessTransactionActor<TData>(
				resolveTransaction,
				validateTransaction
			),
			persist,
			publish,
		},
		guards: {},
		delays: {},
		types: {
			context: {} as StateSyncServerContext<TData>,
			events: {} as StateSyncServerEvent,
		},
	}).createMachine({
		/** @xstate-layout N4IgpgJg5mDOIC5QGMCGA7Abq2BaAtmAC6oSom6xgBOmNAdAJYQA2YAxI+o0Y6i4wBeYANoAGALqJQABwD2sHoznppIAB6IArAGYATPQAsugIx69JwwA5dANgDsAGhABPRIbFj6YqwE4xWr6+5jpaYvb2AL6RzmhYOATEpOSolDR01EzcvPxCXFDsECpgWZhyANYlAGbEyAAWAMIY2LAAsklkJOJSSCDyirwqapoIugbGOmYW1nZOrogmJrY69L46tuEm9oZ6OmK+UTEgcS2JJJ2pVLQMXEq5gvnsNNRymTIs5FWv+PQ1RPVNeJtDopbpqfpKIa9Ea7cZmOw7ex6WweWzONwIKwmbxrbYRfxbfaGaKxZoJQjnFJpa6ZW45AQPdAFACu6BYclIYN6EMGqmh2h2RlM5ksNnWcwxWj0Xlsel8tl8YTEOhstlsJOOZLwFOSFCuGXoqGQvDo7FZ7M5knBCkhfNAIxMVn29CxWllWkCJl8ovRCyW43WyrExi0W0MxKOJ3JIL16QYRpNJRkzIARgJYHVUGmStQwKQXAAVOQABVT6bq7GTacYGa5shtvOGC1CK3WVlsfk8oZMOl9CB75noeisco2EXsvkMyw1Ue1McuccyCcYdHoVfLWbYa7LNbqjyK6BKXDKlW31drVu5DeUdo0zd09DbHf8AUWvfm-Z0SMfKOHQUW7YzlqZy6guNKGsaK5JjuGabtB557kyTzUC8bwfEQXzUD8667nWfTXlC9r3oY9D2PsX4KoYEQqhKCw+AYZFbJM-jSmRehAUCIEXNSBrLquOGwdmZ7lgAYqgjBsBA7C5kQ1AuHhPI3k2-aeAxHb2OsSwTp4Vh9mYYjYtsuweo6jrelYHGnDq3H6vGkH8TBmZCQJdRiRJkDsFmGAHqWCEKQRt4OqppHqZpDgvrpH49nsP5SuGaojqElnRpSsbgXxJSQDwcH0Lm+ZFgAgsgyBgDIRAFtQGCwMuKiwJWLwlbAsAVVVNXoP5AxKfy-Z6IYviPkEwTmFo+KOnpUpWPQujBGsI3DgqFmRsB1lUrZS72ZlEDZc5DVwIoTIteg1WQSohTFKUFRJrtTWHcdvIdbaylTLY9BmKZwZiNKKqRRi-paKRBlOh4VhOrYSzJXOqVgbxG30FlJA7XIjX7VAt1tchqFruhmHYddzWVUdbUPY23XPa9FhYh9X0g+N9iTbKATWBYSK7CYENcati4QYmhrMkQJY0IosAI1uECVVURAFXzAvUELRCwAAysyxVgFlSHPK8WOfN8a6CzWRDE11RH9os-3hus4bGCYI2+H2wQvTo+jBIYoQ+CqOjsytaUwzzqDS8WevCzlYuoBLUv8wHsv64ryslWrBTqEHRAlKHyfUAAFCYnhiAAlOws4c97dm+-7gciyUIdh6XUfCzHKvx4bhF3gg5lDrKOj+LKYNrD9iBYi9BlSpYlHSgEnvzjxxdQbzEdl8H4uS9XcuwG5AhIQeR5YJdus1wbl71p1TcOqbRguyi4ahjbfZWBOQ6hg4o1en149Q5P60l7Pu-z6n4cy8vq+PA1mhbWWEd5y0boFZszos6zSRFoQwiwkTX2REYDupsH6egjKSTiXtoZT1XH7T+ctv5VyIdHABSFE4kGToaCWNBM7ZzzgXXBb9ubT0IX-fWJDF5kNrhQqAECnr6FbMELOoQRoGRdn2fEj4Rrtm9FsSci1sFWQnmtLIdwGTkBvKvDyMk5KCO6r1OEegER6CRCiMQaIorDlevYJU6wwaeEnFgzUOC1FczpHwLRvJdFSXNByCAhjjZSj0k6FYxjjBn2HFE6IRx0ByAgHANQzCPE0mtIfSBCBcDWIxDk7w2dClFJGi-UCrDmBsAyY9UmaxDKBA2H1U2X49JTCHNTDSb0rBTlKTZTx2RvF5CZFUkmxsIh6XCP1ZU6xFR03sF6UxPTObpQ2sMo2zdtjYn2AEEaSI8RYj7Bsf6hJHY6TsL1RZRd37TxcnBVZR9EB00mf4D0ERzFUX2R+II-0xyTisfUj0rjUmv3URlYSu4cp5QgIWEsjk7lZIAgYFUAEDLbDdloPSJz6C2ECAEexKJ9DqiWu44FXNQU3Oco5fIcKnr31Ih4eBKJfAjkWHoFpIM6UTmHFpGYWgLl4KuQ5BCOUXJ+Opd1KwX4cTbNeXskw4y-AumfHKHQU5jAgz5aw0F8NblXkycpR5UqXm7PeXKqK4ZsQXwRHMsIKoNUgthtqoSkLoVFRKmVNGJ0jpiuNsPEiBxHRyi2KGZE40PCrDdFOF5CDZSAuWmkn209HVbhkHjfIHqRn4T1eKyVWyjVvLpqa36JksVhnNg09SdrSWww4ZHYh2ZvXrMnIanZ+aPkYgVAYZEHgIg3w9I7D2RLVEkuWR-ThQchKVx4WO+WSt65bSGbq6pxsukGFzS22V18Ow4hRGDUxKoXa+ErSO9hS8uEToXr-Wt5DxJrygA2h0yIHaBFMRKhB9TWUfhvpNKUwYLDSixF+Nmg6UplPUV4+42iVCisXRmkYOw+y7Emj2QITN9ggyA9EIAA */
		id: "state-sync-server",
		initial: "idle",
		context: {
			// FIXME: This is not at all correct lol.
			data: {} as TData,
			version: 0,

			transactionProcessingPaused: false,
			transactionQueue: [] as StateTransaction[],
			lastTransactionProcessed: null,

			transactionHistoryBuffer: [] as StateTransaction[],
			isPersistenceFailing: false,
		},
		states: {
			idle: {
				description:
					"Canvas room has been started, but is not initialized, *e.g.*, does not have canvas metadata loaded.",
				on: { initialize: { target: "initializing" } },
			},
			initializing: {
				description:
					"Canvas room is initializing, *e.g.*, canvas metadata is being fetched.",
				invoke: {
					src: "fetchState",
					id: "fetchState",
					input: ({ event }) => {
						if (event.type !== "initialize") {
							throw new Error(
								`Requires initialization to occur via an 'initialize' event, got '${event.type}'`
							);
						}
						return event;
					},
					onDone: [
						{
							target: "active",
							actions: [
								assign({
									version: (d) => d.event.output.version,
									data: (d) => d.event.output.data,
									initializationError: undefined,
								}),
							],
						},
					],
					onError: [
						{
							target: "initializationFailed",
							actions: assign({
								initializationError: ({ event }) => {
									logger.error("initialization failing in state machine", {
										error: event.error,
									});
									return event.error;
								},
							}),
						},
					],
				},
				on: { unload: { target: "idle" } },
			},
			active: {
				states: {
					publishable: {
						initial: "readyToPublish",
						states: {
							readyToPublish: { on: { publish: { target: "publishing" } } },
							publishing: {
								invoke: {
									src: "publish",
									id: "publish",
									onDone: [{ target: "readyToPublish" }],
									onError: [{ target: "publishFailed" }],
								},
							},
							publishFailed: {
								on: {
									publish: { target: "publishing" },
									abandonPublish: { target: "readyToPublish" },
								},
							},
						},
					},
					editable: {
						initial: "waitingForTransactions",
						on: {
							enqueueTransaction: {
								actions: assign({
									transactionQueue: ({ context, event }) => [
										...context.transactionQueue,
										{
											id: event.transactionId ?? uuidV4(),
											lastVersionSeen: event.lastVersionSeen,
											patches: event.patches,
										},
									],
								}),
							},
							setTransactionProcessingPaused: {
								actions: assign({
									transactionProcessingPaused: ({ event }) => event.paused,
								}),
							},
						},
						states: {
							waitingForTransactions: {
								always: {
									guard: ({ context }) =>
										!context.transactionProcessingPaused &&
										context.transactionQueue.length > 0,
									target: "processingTransaction",
								},
							},
							processingTransaction: {
								invoke: {
									src: "processTransaction",
									id: "processTransaction",
									input: ({ context }) => ({ context }),
									onDone: [
										{
											target: "waitingForTransactions",
											actions: [
												assign(({ event, context }) => ({
													...context,
													...event.output,
													error: null,
												})),
											],
										},
									],
									onError: [
										{
											target: "waitingForTransactions",
											actions: [
												assign(({ event, context }) => {
													logger.error(
														"processing transaction failing within state machine",
														{
															...event,
														}
													);
													const [
														activeTransaction,
														...newTransactionQueue
													] = context.transactionQueue;
													const newContext = {
														...context,
														transactionQueue: newTransactionQueue,
														error: event.error,
													};
													if (activeTransaction) {
														newContext.lastTransactionProcessed = {
															type: "error",
															error: "unknown",
															message: `Encountered an unknown error: ${event.error}`,
															transaction: activeTransaction,
														};
													}
													return newContext;
												}),
											],
										},
									],
								},
							},
						},
					},
					autoPersistable: {
						initial: "draftAutoPersistsSucceeding",
						states: {
							draftAutoPersistsSucceeding: {
								// 10s
								after: { "10000": { actions: [], target: "flushing" } },
								on: { flush: { target: "flushing" } },
							},
							flushing: {
								invoke: {
									src: "persist",
									id: "persist",
									input: ({ context }) => ({ context, data: context.data }),
									onDone: [
										{
											target: "draftAutoPersistsSucceeding",
											actions: [
												assign(({ context }) => {
													return {
														...context,
														isPersistenceFailing: false,
														error: null,
													};
												}),
											],
										},
									],
									onError: [
										{
											target: "draftAutoPersistsFailing",
											actions: [
												assign(({ context, event }) => {
													logger.error(
														"persistence failing within state machine",
														{
															error: event.error,
														}
													);
													return {
														...context,
														isPersistenceFailing: true,
													};
												}),
											],
										},
									],
								},
							},
							draftAutoPersistsFailing: {
								// 10s
								after: { "10000": { actions: [], target: "flushing" } },
								on: { flush: { target: "flushing" } },
							},
						},
					},
				},
				on: { unload: { target: "idle" } },
				type: "parallel",
			},
			initializationFailed: {
				on: { initialize: { target: "initializing" }, unload: { target: "idle" } },
			},
		},
	});
}

export function waitForTransactionProcessed<TData extends Objectish>(
	sys: ReturnType<typeof createActor<StateSyncServerMachine<TData>>>,
	transactionId: string
) {
	return waitFor(
		sys,
		// IMPORTANT: We do NOT wait for the state to be, e.g., `waitingForTransactions`. This is
		// because `waitFor` does not capture "micro-transactions" like those from `always` states.
		// And in any event, this is enough to capture the first instance that the transaction has
		// been fully processed.
		(snap) => snap.context.lastTransactionProcessed?.transaction.id === transactionId
	);
}
